import type { SupabaseClient } from "@supabase/supabase-js"

import { normalizeIsoCurrency } from "@/lib/app-currency"
import i18n, { resolveAppLanguage } from "@/lib/i18n"
import { convertThroughUsd, roundMoney2, type UsdPerUnitMap } from "@/lib/currency-convert"
import { inferDocumentCategory } from "@/lib/populate-documents-helpers"
import { parseRequirements } from "@/types/admin"
import type { DocumentStatus } from "@/types/document"
import type { TaskCategory, TaskPriority, TaskStatus, TaskType } from "@/types/task"
import { isVisaFreeStatus, isWizardUserVisaIntentStatus } from "@/types/visa-api"
import {
  normalizeVisaEnrichmentPayload,
  type VisaEnrichmentEnvelope,
  type VisaEnrichmentStep,
} from "@/lib/visa-enrichment-normalize"
import { applyManualOverrideToEnvelope } from "@/lib/visa-manual-overrides"
import type { VisaManualOverride } from "@/types/visa-manual-override"
import { withCostHonesty } from "@/lib/cost-certainty"
import { findVisaMetadataRowForRoute } from "@/lib/visa-utils"

type PopulateVisaTaskOptions = {
  nationality: string
  destinationCode: string
  visaType: string
  /** Onboarding / wizard visa status — visa-free routes skip this auto-task. */
  visaStatus: string | undefined | null
  /** Workspace budget ISO code — visa fees (`cost_usd`) are converted into this currency when rates exist. */
  budgetCurrency?: string
  /** Frankfurter-derived map (USD per 1 unit). When null/empty, fees stay in USD. */
  usdPerUnit?: UsdPerUnitMap | null
  /**
   * User's browser locale (e.g., 'en-US', 'pt-BR').
   * If omitted, defaults to 'en-US'.
   */
  locale?: string
  /** ISO-2 residence / application country (often same as nationality); stored on first enriched task when present. */
  originCode?: string
}

function applyUsdFeeToBudget(
  usdAmount: number,
  budgetCurrency: string,
  usdPerUnit: UsdPerUnitMap | null | undefined
): { cost: number; currency: string } {
  const cur = normalizeIsoCurrency(budgetCurrency) ?? "USD"
  if (!usdPerUnit || Object.keys(usdPerUnit).length === 0) {
    return { cost: usdAmount, currency: "USD" }
  }
  const conv = convertThroughUsd(usdAmount, "USD", cur, usdPerUnit)
  if (conv == null) return { cost: usdAmount, currency: "USD" }
  return { cost: roundMoney2(conv), currency: cur }
}

function addDaysFromToday(daysFromNow: number): string {
  const d = new Date()
  d.setDate(d.getDate() + Math.max(0, Math.floor(daysFromNow)))
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

function taskCostFromMetadataRequirements(requirements: unknown): {
  cost: number
  currency: "USD"
} | null {
  const parsed = parseRequirements(requirements)
  const n = parsed.cost_usd
  if (n === undefined || !Number.isFinite(n) || n <= 0) return null
  return { cost: n, currency: "USD" }
}

type VisaSeedTaskRow = {
  workspace_id: string
  title: string
  description: string | null
  status: TaskStatus
  priority: TaskPriority
  due_date: string
  section: string
  notes: string | null
  cost: number | null
  currency: string
  sort_order: number
  custom_fields: Record<string, unknown>
  category: TaskCategory
  task_type: TaskType
}

function getSectionVisaDocumentation(locale?: string): string {
  const lng = resolveAppLanguage((locale ?? "").trim() || "en")
  return i18n.getFixedT(lng)("taskCategories.documentCollection")
}

function getSectionVisaProcess(locale?: string): string {
  const lng = resolveAppLanguage((locale ?? "").trim() || "en")
  return i18n.getFixedT(lng)("taskCategories.visaApplication")
}

type VisaCategoryHint = "student" | "work" | "family" | "digital_nomad" | "tourist" | "general"

function normalizeVisaCategory(visaType: string): VisaCategoryHint {
  const raw = String(visaType ?? "").toLowerCase()
  const hay = raw
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/[_-]+/g, " ")

  if (/\bstudent\b|\bstudy\b|\beducation\b|\buniversity\b/.test(hay)) return "student"
  if (/\bwork\b|\bemploy|\bjob\b|\bworker\b/.test(hay)) return "work"
  if (/\bfamily\b|\bspouse\b|\bpartner\b|\bdependent\b|\breunif/.test(hay)) return "family"
  if (/\bdigital\b.*\bnomad\b|\bnomad\b/.test(hay)) return "digital_nomad"
  if (/\btour\b|\btourist\b|\bvisitor\b|\btransit\b|\bbusiness\b|\bshort\b.*\bstay\b/.test(hay)) return "tourist"
  return "general"
}

/** When false, passport-level visa-free hints must not block seeding (e.g. work visa while short-stay is visa-free). */
export function shouldRespectVisaFreeCacheForVisaType(visaType: string): boolean {
  const cat = normalizeVisaCategory(visaType)
  if (cat !== "tourist" && cat !== "general") return false
  const v = String(visaType ?? "").trim().toLowerCase()
  return v === "visitor" || v === "tourist" || cat === "tourist"
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v)
}

/**
 * Relaxed parse for workspace seeding: normalizes `process_steps` JSON without strict URL validation
 * so legacy rows and slightly imperfect enrichments still produce tasks.
 */
function parseEnrichmentEnvelopeForSeeding(raw: unknown): VisaEnrichmentEnvelope | null {
  return normalizeVisaEnrichmentPayload(raw)
}

function buildNotesFromEnrichedStep(step: VisaEnrichmentStep): string | null {
  const parts: string[] = []
  const n = step.notes.trim()
  if (n.length > 0) parts.push(n)
  const where = step.where.trim()
  if (where.length > 0) parts.push(`Where: ${where}`)
  if (step.dependencies.length > 0) {
    parts.push(`Depends on step order: ${step.dependencies.join(", ")}`)
  }
  if (step.appliesTo.length > 0) {
    parts.push(`Applies to: ${step.appliesTo.join("; ")}`)
  }
  if (step.restrictions.length > 0) {
    parts.push(`Restrictions: ${step.restrictions.join("; ")}`)
  }
  const mistakes = step.commonMistakes.map((m) => m.trim()).filter(Boolean)
  if (mistakes.length > 0) {
    parts.push(`Common mistakes: ${mistakes.join("; ")}`)
  }
  if (step.cityRules.length > 0) {
    for (const cr of step.cityRules) {
      const line = `${cr.city}: ${cr.guidance}`.trim()
      if (line.length > 2) parts.push(line)
    }
  }
  return parts.length > 0 ? parts.join("\n\n") : null
}

function routeLevelCustomFields(
  envelope: VisaEnrichmentEnvelope,
  originCode: string | undefined
): Record<string, unknown> {
  const o: Record<string, unknown> = {
    route_official_sources: envelope.sources,
    route_routing_notes: envelope.routingNotes,
    route_city_rules: envelope.cityRules,
    route_delivery: envelope.delivery,
    route_common_pitfalls: envelope.common_pitfalls,
    route_success_tips: envelope.success_tips,
    route_total_cost_usd: envelope.total_cost_usd,
    route_total_estimated_days: envelope.total_estimated_days,
  }
  const oc = originCode?.trim().toUpperCase()
  if (oc) o.applicant_origin_iso2 = oc
  return o
}

/** Matches `createDocumentSyncFromDocCollectionTask` in `workspace-documents-page.tsx` (+ inferred category). */
function documentDisplayNameFromDocCollectionTaskTitle(title: string): string {
  const t = title.trim()
  const stripped = t.replace(/^(obter|get):\s*/i, "").trim()
  return stripped.length > 0 ? stripped : t
}

type DocCollectionTaskRowForSync = {
  title: string
  description: string | null
  due_date: string | null
  start_date?: string | null
  end_date?: string | null
  custom_fields: unknown
  sort_order: number
}

function buildWorkspaceDocumentInsertFromDocCollectionTask(
  workspaceId: string,
  task: DocCollectionTaskRowForSync
): {
  workspace_id: string
  name: string
  status: DocumentStatus
  category: string
  deadline: string | null
  is_required: boolean
  notes: string | null
  file_url: string | null
  file_size: null
  file_type: null
  sort_order: number
} {
  const cf = isRecord(task.custom_fields) ? task.custom_fields : {}
  const expiryRaw = cf.expiry_date
  const expiry =
    typeof expiryRaw === "string" && expiryRaw.trim() !== "" ? expiryRaw.trim() : null
  const taskRangeDeadline =
    [task.end_date, task.due_date, task.start_date]
      .map((d) => (d != null && String(d).trim() !== "" ? String(d).trim() : null))
      .find(Boolean) ?? null
  const fileRaw = cf.file_url
  const fileUrl = typeof fileRaw === "string" && fileRaw.trim() !== "" ? fileRaw.trim() : null
  const obtained = cf.obtained === true
  const displayName = documentDisplayNameFromDocCollectionTaskTitle(task.title)

  const desc = task.description?.trim() ?? ""
  const legacyNotes =
    typeof cf.notes === "string" && cf.notes.trim() !== "" ? cf.notes.trim() : ""
  const notesMerged =
    desc && legacyNotes ? `${desc}\n\n${legacyNotes}` : desc || legacyNotes || null

  return {
    workspace_id: workspaceId,
    name: displayName.trim(),
    status: obtained ? "obtained" : "needed",
    category: inferDocumentCategory(displayName),
    deadline: expiry ?? taskRangeDeadline,
    is_required: true,
    notes: notesMerged,
    file_url: fileUrl,
    file_size: null,
    file_type: null,
    sort_order: task.sort_order,
  }
}

/**
 * Inserts `visa_application` tasks seeded from `visa_metadata` for this route.
 * - Skipped for visa-free onboarding status.
 * - Uses `visa_metadata.required_documents` (and `requirements.*`) to shape tasks where possible.
 * - Falls back to a standard set based on visa category when metadata is sparse.
 * - When `process_steps` normalizes to a non-empty `steps` array (Gemini/Mistral envelope with optional `sources`, `cityRules`, etc.), seeds visa process tasks from those instead of templates.
 * - Seeds per-document tasks from `required_documents`, then process steps (enriched or templates; templates fall back to `general` when fewer than four process tasks match filters).
 */
export async function populateVisaApplicationTaskFromVisaMetadata(
  workspaceId: string,
  {
    nationality,
    destinationCode,
    visaType,
    visaStatus,
    budgetCurrency: budgetCurrencyOpt,
    usdPerUnit,
    locale: localeOpt,
    originCode: originCodeOpt,
  }: PopulateVisaTaskOptions,
  client: SupabaseClient
): Promise<void> {
  if (
    shouldRespectVisaFreeCacheForVisaType(visaType) &&
    isVisaFreeStatus(visaStatus) &&
    !isWizardUserVisaIntentStatus(visaStatus)
  ) {
    return
  }

  const { row: metaRow, queryError: metaQueryError } = await findVisaMetadataRowForRoute(
    client,
    nationality,
    destinationCode,
    visaType
  )
  if (metaQueryError) {
    return
  }
  if (!metaRow) {
    return
  }

  const row = metaRow as {
    requirements?: unknown
    required_documents?: unknown
    process_steps?: unknown
    destination_currency?: string | null
    manual_override?: VisaManualOverride | null
  }

  const budgetCurrency =
    normalizeIsoCurrency(budgetCurrencyOpt) ??
    normalizeIsoCurrency(row?.destination_currency ?? null) ??
    "USD"
  const requirements = parseRequirements(row?.requirements)
  const requiredDocumentsBase = Array.isArray(row?.required_documents)
    ? row?.required_documents.filter((d): d is string => typeof d === "string" && d.trim().length > 0)
    : []
  const requiredDocuments =
    row.manual_override?.documents_override && Array.isArray(row.manual_override.documents_override)
      ? row.manual_override.documents_override
      : requiredDocumentsBase

  const enrichmentEnvelopeAi = parseEnrichmentEnvelopeForSeeding(row?.process_steps)
  const enrichmentEnvelope =
    enrichmentEnvelopeAi && row.manual_override
      ? applyManualOverrideToEnvelope(enrichmentEnvelopeAi, row.manual_override)
      : enrichmentEnvelopeAi
  const useEnrichedSteps =
    enrichmentEnvelope != null &&
    Array.isArray(enrichmentEnvelope.steps) &&
    enrichmentEnvelope.steps.length > 0

  const documentCollectionRows = requiredDocuments.map((docRaw, idx) => {
    const doc = docRaw.trim()
    const isPortuguese = (localeOpt ?? "").toLowerCase().startsWith("pt")
    return {
      workspace_id: workspaceId,
      title: isPortuguese ? `Obter: ${doc}` : `Get: ${doc}`,
      description: null as string | null,
      status: "todo" as TaskStatus,
      priority: "high" as TaskPriority,
      due_date: addDaysFromToday(14),
      section: getSectionVisaDocumentation(localeOpt),
      notes: null,
      cost: null as number | null,
      currency: budgetCurrency,
      sort_order: idx,
      custom_fields: withCostHonesty(
        {},
        {
          costCertainty: "unknown",
          dateKind: "planning_recommendation",
          requirementSource: "verified_metadata",
        }
      ),
      category: "document_collection" as TaskCategory,
      task_type: "document_collection" as TaskType,
    }
  })

  const sortOffset = documentCollectionRows.length

  let processRows: VisaSeedTaskRow[]

  if (useEnrichedSteps && enrichmentEnvelope) {
    const localeResolved = (localeOpt ?? "").trim() || "en-US"
    const isPortuguese = localeResolved.toLowerCase().startsWith("pt")

    processRows = enrichmentEnvelope.steps.map((step, idx) => {
      const stepTitle = isPortuguese ? (step.title_pt ?? step.title) : step.title

      const stepDescription = isPortuguese
        ? (step.description_pt ?? step.description)
        : step.description

      const fee =
        typeof step.costUsd === "number" && Number.isFinite(step.costUsd) && step.costUsd > 0
          ? applyUsdFeeToBudget(step.costUsd, budgetCurrency, usdPerUnit ?? null)
          : { cost: null as number | null, currency: budgetCurrency }
      return {
        workspace_id: workspaceId,
        title: stepTitle,
        description: stepDescription.trim().length > 0 ? stepDescription : null,
        status: "todo" as TaskStatus,
        priority: (idx < 2 ? "high" : "medium") as TaskPriority,
        due_date: addDaysFromToday(7 + idx * 7),
        section: getSectionVisaProcess(localeOpt),
        notes: buildNotesFromEnrichedStep(step),
        cost: fee.cost,
        currency: fee.cost != null ? fee.currency : budgetCurrency,
        sort_order: sortOffset + idx,
        custom_fields: withCostHonesty(
          {
            visa_step_order: step.order,
            estimated_days: step.estimatedDays,
            documents_needed: step.documentsNeeded,
            official_step_links: step.links,
            applies_to: step.appliesTo,
            step_city_rules: step.cityRules,
            restrictions: step.restrictions,
            step_dependencies: step.dependencies,
            commonMistakes: step.commonMistakes.map((m) => m.trim()).filter(Boolean),
            ...(idx === 0 ? routeLevelCustomFields(enrichmentEnvelope, originCodeOpt) : {}),
          },
          {
            costCertainty: fee.cost != null ? "estimated" : "unknown",
            dateKind: "planning_recommendation",
            requirementSource: "enriched_steps",
          }
        ),
        category: "visa_application" as TaskCategory,
        task_type: "visa_step" as TaskType,
      }
    })
  } else {
    // Do not invent generic template process steps as if they were route-verified.
    // Documents above still come from verified `required_documents` when present.
    const isPortuguese = (localeOpt ?? "").toLowerCase().startsWith("pt")
    const feeUsd = taskCostFromMetadataRequirements(requirements)
    const fee =
      feeUsd != null
        ? applyUsdFeeToBudget(feeUsd.cost, budgetCurrency, usdPerUnit ?? null)
        : { cost: null as number | null, currency: budgetCurrency }
    processRows = [
      {
        workspace_id: workspaceId,
        title: isPortuguese
          ? "Passos do processo ainda não detalhados para esta rota"
          : "Process steps not yet detailed for this route",
        description: isPortuguese
          ? "Temos dados verificados de rota, mas ainda não há passos de processo confirmados. Confirme o procedimento com a fonte oficial e adicione as suas próprias tarefas."
          : "We have verified route data, but confirmed process steps are not available yet. Confirm the procedure with the official source and add your own tasks.",
        status: "todo" as TaskStatus,
        priority: "high" as TaskPriority,
        due_date: addDaysFromToday(7),
        section: getSectionVisaProcess(localeOpt),
        notes: null,
        cost: fee.cost,
        currency: fee.currency,
        sort_order: sortOffset,
        custom_fields: withCostHonesty(
          {
            ...(fee.cost != null ? { fee_label: "application_fee_estimate" } : {}),
          },
          {
            costCertainty: fee.cost != null ? "estimated" : "unknown",
            dateKind: "planning_recommendation",
            requirementSource: "verified_metadata",
          }
        ),
        category: "visa_application" as TaskCategory,
        task_type: "visa_step" as TaskType,
      },
    ]
  }

  const taskRows = [...documentCollectionRows, ...processRows]

  const { data: insertedTasks, error: tasksInsertError } = await client
    .from("workspace_tasks")
    .insert(taskRows as never)
    .select("title, description, due_date, category, custom_fields, sort_order")

  if (tasksInsertError || !insertedTasks?.length) {
    return
  }

  const documentInserts = insertedTasks
    .filter((row) => (row as { category: string }).category === "document_collection")
    .map((row) =>
      buildWorkspaceDocumentInsertFromDocCollectionTask(workspaceId, row as DocCollectionTaskRowForSync)
    )

  if (documentInserts.length > 0) {
    const { error } = await client.from("workspace_documents").insert(documentInserts as never)
    if (error) {
      console.error("workspace_documents insert failed", {
        workspaceId,
        taskContext: {
          documentCollectionTaskCount: documentInserts.length,
          documentNames: documentInserts.map((d) => d.name),
        },
        error,
      })
    }
  }
}
